export type PartnerChartRow = {
    date: string;
    count: number;
    total: number;
    earnings: number;
    platformFees: number;
    supportFees: number;
};

/** Fill every calendar day in [start, end] with zeros, then overlay rows. */
export function fillDailyGaps(
    rows: {
        date: string;
        count: number;
        total: number;
        earnings: number;
        platformFees: number;
        supportFees: number;
    }[],
    startTs: number,
    endTs: number,
): PartnerChartRow[] {
    const byDate: Record<string, PartnerChartRow> = {};

    const current = new Date(startTs);
    current.setHours(0, 0, 0, 0);
    const endDate = new Date(endTs);
    endDate.setHours(0, 0, 0, 0);
    while (current <= endDate) {
        const key = current.toLocaleDateString('en-CA');
        byDate[key] = {
            date: key,
            count: 0,
            total: 0,
            earnings: 0,
            platformFees: 0,
            supportFees: 0,
        };
        current.setDate(current.getDate() + 1);
    }

    for (const row of rows) {
        byDate[row.date] = {
            date: row.date,
            count: row.count,
            total: row.total,
            earnings: row.earnings,
            platformFees: row.platformFees,
            supportFees: row.supportFees,
        };
    }

    return Object.values(byDate).sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
}

/** Monday-start week key as YYYY-MM-DD. */
function weekStartKey(dateStr: string): string {
    const d = new Date(`${dateStr}T00:00:00`);
    const day = d.getDay(); // 0 Sun .. 6 Sat
    const mondayOffset = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + mondayOffset);
    return d.toLocaleDateString('en-CA');
}

/** Sum daily rows into Monday-start weeks. */
export function rollupWeekly(daily: PartnerChartRow[]): PartnerChartRow[] {
    const byWeek: Record<string, PartnerChartRow> = {};
    for (const row of daily) {
        const key = weekStartKey(row.date);
        const existing = byWeek[key];
        if (existing) {
            existing.count += row.count;
            existing.total = Number((existing.total + row.total).toFixed(2));
            existing.earnings = Number((existing.earnings + row.earnings).toFixed(2));
            existing.platformFees = Number((existing.platformFees + row.platformFees).toFixed(2));
            existing.supportFees = Number((existing.supportFees + row.supportFees).toFixed(2));
        } else {
            byWeek[key] = {
                date: key,
                count: row.count,
                total: Number(Number(row.total).toFixed(2)),
                earnings: Number(Number(row.earnings).toFixed(2)),
                platformFees: Number(Number(row.platformFees).toFixed(2)),
                supportFees: Number(Number(row.supportFees).toFixed(2)),
            };
        }
    }
    return Object.values(byWeek).sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
}
